import { useEffect, useCallback, useState } from "react";
import { useLazyQuery, useApolloClient } from "@apollo/client";
import { useHistory } from "react-router";
import queries from "./queries";

/**
 * Custom hook to manage admin authentication
 * - Verifies token on mount
 * - Auto-logs out when token expires
 * - Provides logout function
 */
const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);
  const history = useHistory();
  const client = useApolloClient();

  const [verifyToken] = useLazyQuery(queries.verifyToken, {
    fetchPolicy: "network-only", // Always check with server
    onCompleted: (data) => {
      if (data?.verifyToken?.valid) {
        setIsAuthenticated(true);
        setExpiresAt(data.verifyToken.expiresAt);
      } else {
        // Token is invalid or expired
        logout();
      }
      setIsLoading(false);
    },
    onError: () => {
      // Network error or other issue - log out to be safe
      logout();
      setIsLoading(false);
    },
  });

  const logout = useCallback(() => {
    localStorage.removeItem("adminToken");
    setIsAuthenticated(false);
    setExpiresAt(null);
    // Clear Apollo cache to remove any cached admin data
    client.clearStore();
    history.push("/admin");
  }, [client, history]);

  // Check token on mount
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      verifyToken();
    } else {
      setIsLoading(false);
    }
  }, [verifyToken]);

  // Set up expiration timer
  useEffect(() => {
    if (!expiresAt) return;

    const expirationTime = new Date(expiresAt).getTime();
    const now = Date.now();
    const timeUntilExpiry = expirationTime - now;

    // If already expired, logout immediately
    if (timeUntilExpiry <= 0) {
      logout();
      return;
    }

    // Set a timer to logout when token expires
    // Use a small buffer (5% of remaining time, max 30 seconds) to logout slightly before expiry
    const buffer = Math.min(timeUntilExpiry * 0.05, 30000);
    const timeoutMs = Math.max(timeUntilExpiry - buffer, 1000); // At least 1 second
        
    const timer = setTimeout(() => {
      alert("Your session has expired. Please log in again.");
      logout();
    }, timeoutMs);

    return () => clearTimeout(timer);
  }, [expiresAt, logout]);

  return {
    isAuthenticated,
    isLoading,
    logout,
    expiresAt,
  };
};

export default useAuth;

